import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { PiTrash, PiWarning } from 'react-icons/pi';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import Link from '@/components/Link';
import {
  apiErrorText,
  type ClientCompanyCard,
  type CompanyParty,
  PARTY_ROLE_LABELS,
  type PartyRole,
  useUnlinkParty,
} from '@/models/clientCompanies';
import { useHasAnyPermission } from '@/models/permissions';
import { AddPartyModal } from '@/pages/client-companies/AddPartyModal';

const ROLES: PartyRole[] = ['director', 'shareholder', 'ubo'];

const personName = (p: CompanyParty) =>
  [p.firstName, p.lastName].filter(Boolean).join(' ') ||
  p.email ||
  `Contact #${p.contactId}`;

export function CompanyPeopleCard({
  card,
  canWrite,
}: {
  card: ClientCompanyCard;
  canWrite: boolean;
}) {
  const companyId = card.company.id;
  const [addRole, setAddRole] = useState<PartyRole | null>(null);
  const unlink = useUnlinkParty();
  const canOpenContacts = useHasAnyPermission([
    'contacts:read',
    'contacts:read-all',
    'contacts:*',
    '*:*',
  ]);
  const viewOnly = card.readOnly === true;
  const missingRoles = card.requirements.people
    .filter((p) => !p.filled)
    .map((p) => p.role);

  const remove = async (party: CompanyParty) => {
    try {
      await unlink.mutateAsync({ id: party.id });
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Could not remove',
        message: apiErrorText(e),
      });
    }
  };

  return (
    <CollapsibleCard
      title="People"
      rightSection={
        viewOnly ? undefined : missingRoles.length > 0 ? (
          <Badge size="xs" color="orange" variant="light">
            {missingRoles.length} required missing
          </Badge>
        ) : (
          <Badge size="xs" color="green" variant="light">
            Complete
          </Badge>
        )
      }
    >
      <Stack gap="sm">
        {missingRoles.map((role) => (
          <Alert
            key={role}
            color="orange"
            variant="light"
            p="xs"
            icon={<PiWarning />}
          >
            <Text fz="xs">
              {role === 'shareholder'
                ? 'At least one shareholder is required for delivery'
                : 'At least one director is required for delivery'}
            </Text>
          </Alert>
        ))}
        {ROLES.map((role) => {
          const list = card.parties.filter((p) => p.role === role);
          return (
            <Stack key={role} gap={4}>
              <Group justify="space-between">
                <Text fz="xs" fw={600} c="dimmed" tt="uppercase">
                  {PARTY_ROLE_LABELS[role]}
                  {role !== 'ubo' && !viewOnly && (
                    <Text span c="red">
                      {' '}
                      *
                    </Text>
                  )}
                </Text>
                {canWrite && (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    onClick={() => setAddRole(role)}
                  >
                    Add {PARTY_ROLE_LABELS[role].toLowerCase()}
                  </Button>
                )}
              </Group>
              {list.length === 0 && (
                <Text fz="xs" c="dimmed">
                  None
                </Text>
              )}
              {list.map((p) => (
                <Group key={p.id} justify="space-between" wrap="nowrap">
                  <Group gap={6} wrap="nowrap">
                    {p.contactId ? (
                      canOpenContacts ? (
                        <Link to={`~/contacts/${p.contactId}`}>
                          <Text fz="sm">{personName(p)}</Text>
                        </Link>
                      ) : (
                        <Text fz="sm">{personName(p)}</Text>
                      )
                    ) : p.partyCompanyKind === 'client' ? (
                      <Link to={`~/client-companies/${p.partyCompanyId}`}>
                        <Text fz="sm">{p.partyCompanyName}</Text>
                      </Link>
                    ) : (
                      <Text fz="sm">{p.partyCompanyName}</Text>
                    )}
                    {p.partyCompanyId && (
                      <Badge size="xs" variant="outline" color="gray">
                        {p.partyCompanyKind === 'client'
                          ? 'Client company'
                          : 'External company'}
                      </Badge>
                    )}
                    {p.contactId && p.email && (
                      <Text fz="xs" c="dimmed">
                        {p.email}
                      </Text>
                    )}
                    {p.sharePercent != null && (
                      <Text fz="xs" c="dimmed">
                        {Number(p.sharePercent)}%
                      </Text>
                    )}
                  </Group>
                  {canWrite && (
                    <Tooltip label="Remove link">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        loading={
                          unlink.isPending && unlink.variables?.id === p.id
                        }
                        onClick={() => remove(p)}
                        aria-label="Remove link"
                      >
                        <PiTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              ))}
            </Stack>
          );
        })}
      </Stack>
      {addRole && (
        <AddPartyModal
          companyId={companyId}
          role={addRole}
          onClose={() => setAddRole(null)}
        />
      )}
    </CollapsibleCard>
  );
}
